/**
 * Shared human-readable labels for entity field keys.
 *
 * Used by:
 * - convex/chat/queries.ts — so the chat agent reads + speaks in human
 *   English instead of raw schema keys (`entitlements.unpaidWages`).
 * - src/components/agents/universal/StructuredDiffCard.tsx — so the
 *   Apply/Reject diff card renders friendly field names.
 *
 * Pure module (no Convex server imports, no "use node") so it bundles
 * cleanly into the frontend via the `@cvx` path alias.
 */

/**
 * Explicit overrides, keyed by the raw schema field key (including the
 * dotted nested keys used by the proposal pipeline). Anything not listed
 * falls back to `humanizeFieldKey`.
 */
export const FIELD_LABELS: Record<string, string> = {
  // --- Assets ---
  description: "Description",
  assetType: "Asset type",
  assetStatus: "Status",
  subjectToSecurityInterest: "Subject to security interest",
  location: "Location",
  notes: "Notes",
  assetPhase: "Phase",
  estimatedValue: "Estimated value",

  // --- Contacts ---
  displayName: "Display name",
  email: "Email",
  phone: "Phone",
  abn: "ABN",
  acn: "ACN",
  contactType: "Contact type",

  // --- Liabilities (creditors) ---
  creditorName: "Creditor name",
  amount: "Amount",
  natureOfDebt: "Nature of debt",
  source: "Source",
  confidence: "Confidence",
  date: "Date",
  secured: "Secured",
  securityDetails: "Security details",

  // --- Related parties ---
  legalName: "Legal name",
  partyType: "Party type",
  relationshipStatus: "Relationship status",
  relationshipCategory: "Relationship category",
  summary: "Summary",
  analystNotes: "Analyst notes",
  internalNote: "Internal note",
  confidenceScore: "Confidence score",
  reviewStatus: "Review status",

  // --- Security interests ---
  registrationStatus: "Registration status",
  ppsrRegistrationNumber: "PPSR registration",
  givingOfNoticeIdentifier: "Giving of notice identifier",
  registrationStartDate: "Registration start",
  registrationEndDate: "Registration end",
  agreementDate: "Agreement date",
  collateralType: "Collateral type",
  collateralClass: "Collateral class",
  collateralDescription: "Collateral description",
  fixedOrFloating: "Fixed or floating",
  proceeds: "Proceeds",
  inventory: "Inventory",
  pmsi: "PMSI",
  unperfectedReason: "Unperfected reason",
  unperfectedReasonDetail: "Unperfected detail",
  owedAmount: "Owed amount",
  owedAmountAsAt: "Owed as at",
  "securedParty.name": "Secured party name",
  "securedParty.identifier": "Secured party ID",
  "securedParty.identifierType": "Secured party ID type",

  // --- Loans ---
  counterpartyName: "Counterparty",
  outstandingBalance: "Outstanding balance",
  direction: "Direction",
  agreementStatus: "Agreement status",
  interestRateAnnual: "Interest rate",
  securityStatus: "Security status",
  repaymentTerms: "Repayment terms",

  // --- Employees ---
  name: "Name",
  familyName: "Family name",
  firstName: "First name",
  role: "Role",
  employmentCategory: "Basis of employment",
  employmentType: "Employment type",
  stateOfEmployment: "State of employment",
  startDate: "Start date",
  terminationDate: "End date",
  terminationType: "Termination type",
  notificationDate: "Notification date",
  averageWeeklyHours: "Avg weekly hours",
  wageRate: "Hourly rate",
  regularWeeklyAllowances: "Regular weekly allowances",
  awardClassification: "Award classification",
  dateOfBirth: "Date of birth",
  residencyStatus: "Residency status",
  relationshipToDirector: "Relationship to director",
  isRelatedParty: "Related party",
  "entitlements.unpaidWages": "Unpaid wages",
  "entitlements.unpaidSuper": "Unpaid super",
  "entitlements.accruedAnnualLeave": "Annual leave accrued",
  "entitlements.leaveLoading": "Leave loading",
  "entitlements.accruedLongServiceLeave": "Long-service leave accrued",
  "entitlements.paymentInLieuOfNotice": "Payment in lieu of notice (PILON)",
  "entitlements.redundancyPay": "Redundancy pay",
  "wagesDetail.totalOutstanding": "Total wages outstanding",
  "wagesDetail.unpaidInPeriod": "Unpaid wages in period",
  "wagesDetail.allowancesInPeriod": "Allowances in period",
  "wagesDetail.commissionInPeriod": "Commission in period",
  "wagesDetail.otherEntitlements": "Other wage entitlements",
  "wagesDetail.comments": "Unpaid wages comments",
  "annualLeaveDetail.hoursAtTermination": "Annual leave hours at termination",
  "annualLeaveDetail.windUpCost": "Annual leave winding-up cost",
  "annualLeaveDetail.comments": "Annual leave comments",
  "leaveLoadingDetail.ratePercent": "Leave loading rate",
  "leaveLoadingDetail.windUpCost": "Leave loading winding-up cost",
  "leaveLoadingDetail.comments": "Leave loading comments",
  "longServiceLeaveDetail.hoursAtTermination":
    "Long-service leave hours at termination",
  "longServiceLeaveDetail.windUpCost": "Long-service leave winding-up cost",
  "longServiceLeaveDetail.comments": "Long-service leave comments",
  "pilnDetail.weeksOwing": "Notice weeks owing",
  "pilnDetail.windUpCost": "Notice winding-up cost",
  "pilnDetail.comments": "Payment in lieu of notice comments",
  "redundancyDetail.weeksOwing": "Redundancy weeks owing",
  "redundancyDetail.windUpCost": "Redundancy winding-up cost",
  "redundancyDetail.comments": "Redundancy comments",
  "paymentsAfterTermination.unpaidWages": "Unpaid wages already paid",
  "paymentsAfterTermination.annualLeave": "Annual leave already paid",
  "paymentsAfterTermination.longServiceLeave":
    "Long-service leave already paid",
  "paymentsAfterTermination.piln": "Notice already paid",
  "paymentsAfterTermination.redundancy": "Redundancy already paid",
  "paymentsAfterTermination.comments": "Payments after termination comments",

  // --- ATO/SRO ---
  authorityLabel: "Authority label",
  authority: "Authority",
  "runningBalance.amount": "Running balance",
  "companyBalance.amount": "Company balance",

  // --- Board minutes & reports ---
  documentType: "Document type",
  flagDetails: "Flag details",

  // --- Solvency analysis (financial periods + indicators) ---
  periodLabel: "Period label",
  periodType: "Period type",
  endDate: "End date",
  ebitda: "EBITDA",
  ebit: "EBIT",
  assessment: "Assessment",
  reasoning: "Reasoning",
  earliestIndicationDate: "Earliest indication date",
};

/**
 * Allowed enum values per (table, field key). Mirrors the schema union
 * validators so the chat agent picks a value the schema will accept
 * instead of guessing (e.g. "full_time" when the schema wants "FT").
 *
 * Keyed by table because the same field name can be a different enum on
 * different tables (e.g. `partyType` is individual/company/trust on
 * related parties but related_party/third_party on security interests).
 *
 * Keep in sync with each table's schema file. Number/boolean/free-text fields
 * are intentionally absent — only closed enums belong here.
 */
export const FIELD_ENUM_OPTIONS: Record<string, Record<string, string[]>> = {
  assetEntries: {
    assetType: [
      "Cash at Bank",
      "Accounts Receivable",
      "Assets Subject to Lease",
      "Interests in Land",
      "Plant and Equipment",
      "Stock / Inventory",
      "Intangible Assets",
      "Contingent Assets",
    ],
    assetStatus: ["Identified", "Missing"],
    assetPhase: ["Pre-Appointment", "Post-Appointment"],
  },
  liabilityEntries: {
    natureOfDebt: [
      "trade",
      "lease",
      "tax",
      "utility",
      "professional_fees",
      "loan",
      "insurer",
      "other",
    ],
    confidence: ["high", "medium", "low"],
    source: [
      "aged_payables",
      "invoice",
      "contract",
      "ledger",
      "bank_statement",
      "director_disclosure",
      "proof_of_debt",
      "other",
    ],
  },
  relatedPartyEntries: {
    partyType: ["individual", "company", "trust"],
    relationshipStatus: ["confirmed", "probable", "possible", "excluded"],
    relationshipCategory: [
      "director",
      "former_director",
      "shareholder",
      "related_body_corporate",
      "director_relative",
      "spouse_of_director",
      "controlled_entity",
      "associate",
      "unknown_associated_party",
      "spouse",
      "de_facto_partner",
      "former_spouse",
      "former_de_facto_partner",
      "relative",
    ],
    reviewStatus: [
      "system_generated",
      "under_review",
      "confirmed_by_user",
      "excluded_by_user",
    ],
  },
  securityInterestEntries: {
    partyType: ["related_party", "third_party"],
    registrationStatus: ["registered", "unperfected"],
    collateralType: ["commercial_property", "consumer_property"],
    collateralClass: [
      "motor_vehicle",
      "allpap_no_exceptions",
      "allpap_with_exceptions",
      "other_goods",
      "account",
      "intangible_property",
      "financial_property",
      "chattel_paper",
      "general_intangible",
      "inventory",
      "negotiable_instrument",
    ],
    fixedOrFloating: ["fixed", "floating"],
    unperfectedReason: [
      "no_ppsr_registration",
      "registration_expired",
      "registration_defective",
      "wrong_collateral_class",
    ],
  },
  loanEntries: {
    direction: ["Loan_Granted", "Loan_Taken"],
    agreementStatus: ["Formal_Contract", "Invoice_Only", "No_Documentation"],
    securityStatus: ["Unsecured", "PPSR_Registered", "Mortgage_Registered"],
    repaymentTerms: [
      "Interest_Only",
      "Principal_And_Interest",
      "At_Call",
      "Balloon",
    ],
  },
  employeeEntries: {
    employmentCategory: [
      "FT",
      "PT",
      "Casual",
      "Shiftwork",
      "Probation",
      "Other",
    ],
    employmentType: [
      "employee",
      "apprentice",
      "trainee",
      "contractor",
      "tcf_outworker",
    ],
    stateOfEmployment: ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"],
    residencyStatus: [
      "citizen",
      "permanent_resident",
      "temporary_visa",
      "other",
    ],
    relationshipToDirector: [
      "none",
      "parent",
      "grandparent",
      "sibling",
      "child",
      "grandchild",
      "spouse",
      "other",
    ],
    terminationType: ["notification", "no_notification", "resignation"],
  },
  atoSroEntries: {
    authority: [
      "ato",
      "sro_vic",
      "sro_nsw",
      "sro_qld",
      "sro_wa",
      "sro_sa",
      "sro_tas",
      "sro_act",
      "sro_nt",
      "other",
    ],
  },
  boardReportFindings: {
    documentType: [
      "board_minutes",
      "board_report",
      "circular_resolution",
      "pre_appointment_minutes",
    ],
    confidence: ["high", "medium", "low"],
  },
  financialPeriodEntries: {
    periodType: ["annual", "quarterly", "monthly", "weekly"],
  },
  financialIndicatorEntries: {
    assessment: ["met", "not_met", "insufficient_data"],
    confidence: ["high", "medium", "low"],
  },
};

/** Returns the allowed enum values for a field, or null when it's not a closed enum. */
export function getFieldOptions(table: string, key: string): string[] | null {
  return FIELD_ENUM_OPTIONS[table]?.[key] ?? null;
}

/**
 * Synonyms for enum fields, keyed by field key then by a *normalized*
 * input string (lower-cased, non-alphanumerics stripped). Lets common
 * human / loose-model phrasings map onto the exact schema literal —
 * e.g. "Full time" / "full_time" / "fulltime" all become "FT".
 */
const ENUM_SYNONYMS: Record<string, Record<string, string>> = {
  employmentCategory: {
    fulltime: "FT",
    full: "FT",
    permanent: "FT",
    parttime: "PT",
    part: "PT",
    casual: "Casual",
    contractor: "Casual",
  },
  direction: {
    granted: "Loan_Granted",
    loangranted: "Loan_Granted",
    lent: "Loan_Granted",
    taken: "Loan_Taken",
    loantaken: "Loan_Taken",
    borrowed: "Loan_Taken",
  },
  residencyStatus: {
    citizen: "citizen",
    australiancitizen: "citizen",
    pr: "permanent_resident",
    permanentresident: "permanent_resident",
    temporary: "temporary_visa",
    temporaryvisa: "temporary_visa",
    visa: "temporary_visa",
  },
};

const normalizeEnumInput = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Coerces a proposed value onto the canonical schema literal for an enum
 * field. Returns the value unchanged for non-enum fields and null/undefined.
 * Throws a clean, human-readable error if it can't be mapped.
 *
 * Match order: exact → case/punctuation-insensitive → synonym table.
 */
export function coerceEnumValue(
  table: string,
  key: string,
  value: unknown,
): unknown {
  const options = getFieldOptions(table, key);
  if (!options) return value;
  if (value === undefined || value === null) return value;

  if (typeof value === "string") {
    if (options.includes(value)) return value;
    const target = normalizeEnumInput(value);
    // Case / punctuation-insensitive match against the options themselves.
    const direct = options.find((o) => normalizeEnumInput(o) === target);
    if (direct) return direct;
    // Synonym table.
    const syn = ENUM_SYNONYMS[key]?.[target];
    if (syn && options.includes(syn)) return syn;
  }

  throw new Error(
    `"${String(value)}" is not a valid value for ${key}. Allowed: ${options.join(", ")}.`,
  );
}

/** Acronyms that should stay upper-cased when humanizing unknown keys. */
const ACRONYMS = new Set([
  "abn",
  "acn",
  "ppsr",
  "pmsi",
  "ato",
  "sro",
  "id",
  "gst",
  "payg",
]);

/**
 * Turns a raw schema field key into a human-readable label. Prefers the
 * explicit `FIELD_LABELS` map; otherwise splits camelCase / snake_case /
 * dotted keys into spaced words, upper-casing known acronyms.
 *
 * Examples:
 *   "wageRate"                  -> "Wage rate"
 *   "entitlements.unpaidSuper"  -> "Unpaid super"
 *   "abn"                       -> "ABN"
 */
export function humanizeFieldKey(key: string): string {
  const mapped = FIELD_LABELS[key];
  if (mapped) return mapped;

  // For dotted keys, label from the last segment (the leaf field).
  const leaf = key.includes(".") ? (key.split(".").pop() ?? key) : key;

  const words = leaf
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/);

  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (ACRONYMS.has(lower)) return w.toUpperCase();
      if (i === 0) return w.charAt(0).toUpperCase() + w.slice(1);
      return lower;
    })
    .join(" ");
}
